// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import AddressSuggestion from "@gcg/App/Components/AddressSuggestionCard/AddressSuggestion";
import validateAddressWithAvalara from "@gcg/App/Handlers/ValidateAddresswithAvalara";
import { AddressValidationModel } from "@gcg/App/Services/AddressValidationWithAvalaraService";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCurrentCountries } from "@insite/client-framework/Store/Data/Countries/CountriesSelectors";
import { BillToModel } from "@insite/client-framework/Types/ApiModels";
import React, { forwardRef, useImperativeHandle, useRef } from "react";
import { connect, ResolveThunks } from "react-redux";

interface OwnProps {
    addressSuggestionModalIsOpen?: boolean;
    validate?: any;
    title?: string;
}

const mapStateToProps = (state: ApplicationState) => ({
    countries: getCurrentCountries(state),
});

const mapDispatchToProps = {
    validateAddressWithAvalara,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

const AvalaraValidationModal = forwardRef<OwnProps, Props>(({ validateAddressWithAvalara, title, countries }, ref) => {
    const [addressSuggestionModalIsOpen, setAddressSuggestionModalIsOpen] = React.useState(false);
    const [addressSuggestionList, setAddressSuggestionList] = React.useState([] as AddressValidationModel[]);
    const [addressValidationErrorModalIsOpen, setAddressValidationErrorModalIsOpen] = React.useState(false);
    const [addressValidationError, setAddressValidationError] = React.useState("");
    const [selectedAddressSuggestionId, setSelectedAddressSuggestionId] = React.useState("");
    const [formAddressDraft, setFormAddressDraft] = React.useState({} as BillToModel);

    const resolve = useRef((address?: any): any => {});

    const saveSelectedAddressSuggestion = (id: string | undefined) => {
        const selectedAddressSuggestion = addressSuggestionList.find(address => {
            return address.id === (id || selectedAddressSuggestionId);
        });
        if (selectedAddressSuggestion) {
            const currentCountry = countries?.find(
                country => country.abbreviation === selectedAddressSuggestion.country,
            );
            const currentState = currentCountry?.states?.find(
                state =>
                    state.abbreviation === selectedAddressSuggestion.state ||
                    state.abbreviation === selectedAddressSuggestion.region,
            );
            const address = {
                address1: selectedAddressSuggestion.addressLine1 || selectedAddressSuggestion.line1,
                address2: selectedAddressSuggestion.addressLine2 || selectedAddressSuggestion.line2,
                address3: selectedAddressSuggestion.addressLine3 || selectedAddressSuggestion.line3,
                city: selectedAddressSuggestion.city,
                country: {
                    abbreviation: currentCountry?.abbreviation,
                    id: currentCountry?.id,
                    name: currentCountry?.name,
                    properties: {},
                    uri: currentCountry?.uri,
                },
                postalCode: selectedAddressSuggestion.postalCode,
                state: {
                    abbreviation: currentState?.abbreviation,
                    id: currentState?.id,
                    name: currentState?.name,
                    properties: {},
                    uri: currentState?.uri,
                },
            };
            addressSuggestionModalCloseHandler();
            resolve.current({ ...formAddressDraft, ...address });
        }
    };

    const addressSuggestionModalCloseHandler = () => {
        setAddressSuggestionModalIsOpen(false);
    };

    const addressValidationErrorModalCloseHandler = () => {
        setAddressValidationErrorModalIsOpen(false);
    };

    const addressSuggestionsSkip = () => {
        addressSuggestionModalCloseHandler();
        resolve.current(formAddressDraft);
    };

    const validate = (address: any, onModalOpen: any) => {
        let country = "";
        let state = "";

        if (address.country) {
            if (typeof address.country === "string") {
                country = address.country;
            } else {
                country = address.country?.abbreviation;
            }
        }

        if (address.state) {
            if (typeof address.state === "string") {
                state = address.state;
            } else {
                state = address.state?.abbreviation;
            }
        }

        return new Promise((res: any, reject: any) => {
            resolve.current = res;
            validateAddressWithAvalara({
                address: {
                    addressLine1: address.address1 || "",
                    addressLine2: address.address2 || "",
                    addressLine3: address.address3 || "",
                    city: address.city,
                    country,
                    postalCode: address.postalCode,
                    state,
                },
                onComplete: response => {
                    let localResults: {};

                    try {
                        localResults = JSON.parse(response?.apiResult?.errorJson?.message);
                    } catch (e) {
                        localResults = {};
                    }
                    if ((localResults as any).skipAddressValidation) {
                        resolve.current(address);
                    } else if (response?.apiResult?.status === 400) {
                        const error =
                            JSON.parse(response?.apiResult?.errorMessage)?.message || response?.apiResult?.errorMessage;
                        setAddressValidationError(error);
                        setAddressValidationErrorModalIsOpen(true);
                        reject(error);
                    } else if (response?.apiResult?.isSameOriginalAddress) {
                        resolve.current(address);
                    } else if (response?.apiResult?.validatedAddresses?.length) {
                        onModalOpen && onModalOpen();
                        setFormAddressDraft(address);
                        setAddressSuggestionList(
                            response.apiResult.validatedAddresses.map(
                                (validatedAddress: AddressValidationModel, index: number) => {
                                    (validatedAddress as any).id = `${index}, ${
                                        validatedAddress.addressLine1 || validatedAddress.line1
                                    },  ${validatedAddress.city}, ${
                                        validatedAddress.state || validatedAddress.region
                                    }  ${validatedAddress.postalCode}, ${validatedAddress.country}`;
                                    return validatedAddress;
                                },
                            ),
                        );
                        setAddressSuggestionModalIsOpen(true);
                    }
                },
            });
        });
    };

    useImperativeHandle(ref, () => ({ validate }));

    return (
        <AddressSuggestion
            addressSuggestionModalIsOpen={addressSuggestionModalIsOpen}
            addressSuggestionModalCloseHandler={addressSuggestionModalCloseHandler}
            selectedAddressSuggestionId={selectedAddressSuggestionId}
            saveSelectedAddressSuggestion={saveSelectedAddressSuggestion}
            addressSuggestionList={addressSuggestionList}
            setSelectedAddressSuggestionId={setSelectedAddressSuggestionId}
            addressValidationError={addressValidationError}
            addressValidationErrorModalIsOpen={addressValidationErrorModalIsOpen}
            addressValidationErrorModalCloseHandler={addressValidationErrorModalCloseHandler}
            addressSuggestionsSkip={addressSuggestionsSkip}
            title={title}
        />
    );
});

AvalaraValidationModal.displayName = "AvalaraValidation";

export default connect(mapStateToProps, mapDispatchToProps, null, { forwardRef: true })(AvalaraValidationModal);
